<template>
  <div class="error-boundary">
    <slot v-if="!hasError" />
    <div v-else class="error-boundary__fallback" role="alert">
      <div class="error-boundary__content">
        <i class="pi pi-exclamation-circle error-boundary__icon" aria-hidden="true"></i>
        <h2 class="error-boundary__title">Something went wrong</h2>
        <p class="error-boundary__message">
          {{ errorMessage }}
        </p>
        <div class="error-boundary__actions">
          <Button
            label="Try Again"
            icon="pi pi-refresh"
            severity="secondary"
            @click="handleReset"
            aria-label="Reset and try again"
          />
          <Button
            label="Go Home"
            icon="pi pi-home"
            severity="secondary"
            text
            @click="handleGoHome"
            aria-label="Return to home page"
          />
        </div>
        <details v-if="isDevelopment && errorDetails" class="error-boundary__details">
          <summary>Error Details (Development Only)</summary>
          <pre class="error-boundary__stack">{{ errorDetails }}</pre>
        </details>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onErrorCaptured, computed } from 'vue';
import { useRouter } from 'vue-router';
import Button from 'primevue/button';

// ============================================================================
// State
// ============================================================================

const hasError = ref(false);
const errorMessage = ref('An unexpected error occurred. Please try again.');
const errorDetails = ref<string | null>(null);
const router = useRouter();

// ============================================================================
// Computed
// ============================================================================

const isDevelopment = computed(() => {
  return import.meta.env.DEV;
});

// ============================================================================
// Error Handling
// ============================================================================

onErrorCaptured((err: Error, instance, info) => {
  hasError.value = true;
  
  // Set user-friendly message
  errorMessage.value = getUserFriendlyMessage(err);
  
  // Store error details for development
  if (isDevelopment.value) {
    errorDetails.value = `${err.message}\n\nStack:\n${err.stack}\n\nComponent: ${info}`;
  }
  
  // Log error to console for debugging
  console.error('Error caught by ErrorBoundary:', {
    error: err,
    instance,
    info,
    message: err.message,
    stack: err.stack,
  });
  
  // Prevent error from propagating
  return false;
});

// ============================================================================
// Methods
// ============================================================================

function getUserFriendlyMessage(error: Error): string {
  const message = error.message.toLowerCase();
  
  // Network errors
  if (message.includes('network') || message.includes('fetch')) {
    return 'Unable to connect to the server. Please check your internet connection and try again.';
  }
  
  // Authentication errors
  if (message.includes('auth') || message.includes('unauthorized')) {
    return 'Your session has expired. Please log in again.';
  }
  
  // Validation errors
  if (message.includes('validation') || message.includes('invalid')) {
    return 'The data provided is invalid. Please check your input and try again.';
  }
  
  // Default message
  return 'An unexpected error occurred. Please try again or contact support if the problem persists.';
}

function handleReset(): void {
  hasError.value = false;
  errorMessage.value = 'An unexpected error occurred. Please try again.';
  errorDetails.value = null;
}

function handleGoHome(): void {
  hasError.value = false;
  errorMessage.value = 'An unexpected error occurred. Please try again.';
  errorDetails.value = null;
  router.push('/');
}
</script>

<style scoped>
.error-boundary {
  width: 100%;
  height: 100%;
}

.error-boundary__fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  padding: 2rem;
  background-color: #FAFAFA;
}

.error-boundary__content {
  max-width: 600px;
  text-align: center;
  background: white;
  padding: 3rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.error-boundary__icon {
  font-size: 4rem;
  color: #DC062F;
  margin-bottom: 1.5rem;
}

.error-boundary__title {
  font-family: 'Inter', Arial, sans-serif;
  font-weight: 600;
  font-size: 24px;
  color: #2F405B;
  margin-bottom: 1rem;
}

.error-boundary__message {
  font-family: 'Nunito', Arial, sans-serif;
  font-weight: 400;
  font-size: 16px;
  color: #2F405B;
  margin-bottom: 2rem;
  line-height: 1.6;
}

.error-boundary__actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}

.error-boundary__details {
  margin-top: 2rem;
  text-align: left;
  background-color: #F5F5F5;
  padding: 1rem;
  border-radius: 4px;
  border: 1px solid #E0E0E0;
}

.error-boundary__details summary {
  font-family: 'Inter', Arial, sans-serif;
  font-weight: 600;
  font-size: 14px;
  color: #2F405B;
  cursor: pointer;
  user-select: none;
}

.error-boundary__details summary:hover {
  color: #4AA483;
}

.error-boundary__stack {
  font-family: 'Consolas', 'Courier New', monospace;
  font-size: 12px;
  color: #2F405B;
  margin-top: 1rem;
  overflow-x: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
}

/* Responsive Design */
@media (max-width: 768px) {
  .error-boundary__fallback {
    min-height: 300px;
    padding: 1rem;
  }

  .error-boundary__content {
    padding: 2rem 1.5rem;
  }

  .error-boundary__icon {
    font-size: 3rem;
  }

  .error-boundary__title {
    font-size: 20px;
  }

  .error-boundary__message {
    font-size: 14px;
  }

  .error-boundary__actions {
    flex-direction: column;
    width: 100%;
  }

  .error-boundary__actions :deep(.p-button) {
    width: 100%;
  }
}
</style>
